const QUEUE_KEY = 'fairway.outbox.v1';

/**
 * Where the backend lives.
 *
 * The web build is served BY the worker, so a relative path is right there. The
 * native build is not: it runs from `capacitor://localhost`, where a relative
 * `/api/...` resolves against the app bundle on the phone and fails. Every API
 * call in the iOS app was failing this way, and because the failure looks like
 * a network error it read as "the map service is busy" rather than as a bug.
 *
 * Detected at runtime rather than baked in at build time, so one `npm run
 * build` produces a bundle that is correct on both surfaces.
 */
const HOSTED = 'https://mercury-fairway.mercury-jed.workers.dev';

function resolveBase(): string {
  // Typed structurally, not via vite/client: import.meta.env only exists
  // under Vite; node --test (which imports this file transitively) has
  // import.meta but no env, and the test tsconfig has no Vite types.
  const configured = (import.meta as { env?: { VITE_API_BASE?: string } }).env?.VITE_API_BASE;
  if (configured) return configured;
  if (typeof location === 'undefined') return '';
  // capacitor: on iOS, http: on the Android WebView's local server — neither is
  // an origin this API is served from.
  return /^https?:$/.test(location.protocol) && !/^localhost$/.test(location.hostname)
    ? ''
    : HOSTED;
}

export const API_BASE = resolveBase();

export class ApiError extends Error {
  readonly status: number;
  // Not a parameter property: Node's built-in type stripping (node --test on
  // 26) refuses constructor(readonly ...) syntax, and the tests import this.
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

function headers(): HeadersInit {
  const h: Record<string, string> = { 'content-type': 'application/json' };
  const key = localStorage.getItem('fairway.deviceKey');
  const buggy = localStorage.getItem('fairway.buggyId');
  if (key) h['x-fairway-key'] = key;
  if (buggy) h['x-fairway-buggy'] = buggy;
  return h;
}

export async function apiGet<T>(path: string, timeoutMs = 8000): Promise<T> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const r = await fetch(API_BASE + path, { headers: headers(), signal: ctl.signal });
    if (!r.ok) throw new ApiError(await r.text(), r.status);
    return (await r.json()) as T;
  } finally {
    clearTimeout(t);
  }
}

export async function apiPost<T>(path: string, body: unknown, timeoutMs = 8000): Promise<T> {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), timeoutMs);
  try {
    const r = await fetch(API_BASE + path, {
      method: 'POST',
      headers: headers(),
      body: JSON.stringify(body),
      signal: ctl.signal,
    });
    if (!r.ok) throw new ApiError(await r.text(), r.status);
    return (await r.json()) as T;
  } finally {
    clearTimeout(t);
  }
}

interface QueuedCall {
  id: string;
  path: string;
  body: unknown;
  queuedAt: number;
  tries: number;
}

/**
 * Golf courses have dead spots. Every write goes through here: try the network,
 * and if it fails park it in localStorage and drain later. Scores are never
 * lost because the network dropped behind the 7th green.
 */
export async function apiPostDurable(path: string, body: unknown): Promise<'sent' | 'queued'> {
  try {
    await apiPost(path, body, 6000);
    return 'sent';
  } catch {
    const q = readQueue();
    q.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      path,
      body,
      queuedAt: Date.now(),
      tries: 0,
    });
    writeQueue(q.slice(-500));
    return 'queued';
  }
}

export async function drainOutbox(): Promise<{ sent: number; left: number }> {
  let q = readQueue();
  if (!q.length) return { sent: 0, left: 0 };

  let sent = 0;
  const remaining: QueuedCall[] = [];
  for (const call of q) {
    try {
      await apiPost(call.path, call.body, 6000);
      sent++;
    } catch (e) {
      const status = e instanceof ApiError ? e.status : 0;
      // 4xx other than 408/429 will never succeed on retry — drop it rather
      // than jamming the queue forever.
      const permanent = status >= 400 && status < 500 && status !== 408 && status !== 429;
      if (!permanent && call.tries < 50) remaining.push({ ...call, tries: call.tries + 1 });
    }
  }
  writeQueue(remaining);
  return { sent, left: remaining.length };
}

export function outboxSize(): number {
  return readQueue().length;
}

function readQueue(): QueuedCall[] {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? (JSON.parse(raw) as QueuedCall[]) : [];
  } catch {
    return [];
  }
}

function writeQueue(q: QueuedCall[]) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(q));
  } catch {
    /* storage full — nothing useful to do on a head unit */
  }
}
